import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useReader } from "../../context/ReaderContext";
import { api } from "../../lib/api";
import { track } from "../../lib/analytics";
import { BrandIcon } from "./Logo";

// The single authoritative welcome popup. Timer state lives in sessionStorage so route changes never reset it.
const SKIP = /^\/(admin|redakcija|partner|prijava|registracija|zaboravljena-lozinka|premium|moj-profil|moj-balkan-info|postavke-privatnosti|newsletter|vertraege-kuendigen|kontakt|ai\/razgovor)(\/|$)/;
const K = { active: "bp_wp_active_ms", next: "bp_wp_next_ms", optout: "bp_wp_optout" };
const BUSY = '[role=dialog][data-state=open], [role=alertdialog], [data-testid=consent-banner], [aria-busy="true"]';
const ss = { get: (k) => sessionStorage.getItem(k), set: (k, v) => sessionStorage.setItem(k, String(v)) };
const typing = () => { const a = document.activeElement; return !!a && (/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) || a.isContentEditable); };

export const WelcomePopupCard = ({ cfg, onCta, onContinue, optOut, setOptOut, preview = false }) => {
  const Title = preview ? "h2" : DialogTitle;
  const Desc = preview ? "p" : DialogDescription;
  return (
    <div className="relative overflow-hidden bg-navy text-white" data-testid={preview ? "welcome-preview" : "welcome-card"}>
      <div aria-hidden className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-pulse/25 blur-2xl" />
      <div className="relative p-6 sm:p-8">
        <div className="flex items-center gap-2"><BrandIcon className="h-8 w-8" testId="welcome-brand" /><span className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/80">BalkanPuls.de · Premium</span></div>
        <Title className="mt-5 font-display text-[1.7rem] font-bold leading-[1.1] tracking-tight text-white sm:text-[2.1rem]" data-testid="welcome-title">{cfg.title}</Title>
        <Desc className="mt-3 text-[15px] leading-relaxed text-white/85" data-testid="welcome-description">{cfg.description}</Desc>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-testid="welcome-highlights">{cfg.highlights.map((h) => <li key={h} className="flex items-start gap-2 text-sm font-semibold"><span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-pulse"><Check className="h-3.5 w-3.5" aria-hidden /></span>{h}</li>)}</ul>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button type="button" onClick={onCta} className="rounded-sm bg-pulse px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-pulse-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-testid="welcome-trial-cta">{cfg.cta_label}</button>
          <button type="button" onClick={onContinue} className="rounded-sm border border-white/30 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-testid="welcome-continue">{cfg.secondary_label}</button>
        </div>
        {cfg.session_opt_out && <label className="mt-4 flex items-center gap-2 text-xs text-white/75"><input type="checkbox" checked={!!optOut} onChange={(e) => setOptOut?.(e.target.checked)} className="accent-pulse" data-testid="welcome-optout" />Ne prikazuj ponovo tokom ove posjete.</label>}
      </div>
    </div>
  );
};

export function WelcomePopup() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { reader } = useReader();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [optOut, setOptOut] = useState(false);
  const st = useRef({});
  st.current = { cfg, open, reader, pathname };
  useEffect(() => {
    localStorage.removeItem("bp_welcome_v2");
    api.get("/welcome-popup").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  useEffect(() => {
    const id = setInterval(() => {
      const { cfg: c, open: o, reader: r, pathname: p } = st.current;
      if (!c?.enabled || o || r !== false || ss.get(K.optout) || SKIP.test(p) || document.visibilityState !== "visible") return;
      if (!(window.matchMedia("(max-width: 767px)").matches ? c.mobile : c.desktop)) return;
      const blocked = typing() || !!document.querySelector(BUSY);
      if (blocked) return;
      const active = Number(ss.get(K.active) || 0) + 1000;
      ss.set(K.active, active);
      const due = Number(ss.get(K.next) || c.first_delay_s * 1000);
      if (active < due) return;
      setOpen(true);
      track("welcome_popup_viewed");
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const dismiss = (method, to) => {
    setOpen(false);
    ss.set(K.active, 0);
    ss.set(K.next, (cfg?.repeat_s || 120) * 1000);
    if (optOut) ss.set(K.optout, "1");
    track("welcome_popup_dismissed", { method });
    if (to) nav(to);
  };
  if (!cfg) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss("close"); }}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-xl gap-0 overflow-y-auto rounded-md border-0 p-0 motion-reduce:animate-none [&>button]:text-white [&>button]:opacity-90" data-testid="welcome-popup">
        <WelcomePopupCard cfg={cfg} optOut={optOut} setOptOut={setOptOut} onCta={() => { track("trial_offer_selected"); dismiss("trial", cfg.cta_url); }} onContinue={() => dismiss("continue")} />
      </DialogContent>
    </Dialog>
  );
}
